import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../database/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../database/firebase';

// Async thunk to create a new customer
export const createCustomer = createAsyncThunk(
  'customer/createCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      // Create authentication user if email and password provided
      let authUserId = null;
      if (customerData.email && customerData.password) {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          customerData.email,
          customerData.password
        );
        authUserId = userCredential.user.uid;
      }

      // Prepare customer data
      const { password, confirmPassword, ...customerInfo } = customerData;

      const customerDoc = {
        ...customerInfo,
        authUserId,
        isVerified: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Shake tracking
        shakeQuantity: {
          available: 0,
          topupDate: null,
          topupAmount: 0,
          remainingDays: 0,
        },
        // Subscription
        subscription: {
          type: null,
          startDate: null,
          endDate: null,
          amount: 0,
          isActive: false,
        },
      };

      const docRef = await addDoc(collection(db, 'users'), customerDoc);

      return { id: docRef.id, ...customerDoc };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch customer details
export const fetchCustomerDetails = createAsyncThunk(
  'customer/fetchCustomerDetails',
  async (customerId, { rejectWithValue }) => {
    try {
      const customerDoc = await getDoc(doc(db, 'users', customerId));

      if (!customerDoc.exists()) {
        return rejectWithValue('Customer not found');
      }

      // Fetch body metrics history
      const metricsRef = collection(db, 'users', customerId, 'bodyMetrics');
      const metricsQuery = query(metricsRef, orderBy('date', 'desc'));
      const metricsSnapshot = await getDocs(metricsQuery);

      const bodyMetrics = [];
      metricsSnapshot.forEach((doc) => {
        bodyMetrics.push({ id: doc.id, ...doc.data() });
      });

      // Fetch order history
      const ordersRef = collection(db, 'recharges');
      const ordersQuery = query(ordersRef, where('userId', '==', customerId), orderBy('createdAt', 'desc'));
      const ordersSnapshot = await getDocs(ordersQuery);

      const orders = [];
      ordersSnapshot.forEach((doc) => {
        orders.push({ id: doc.id, ...doc.data() });
      });

      return {
        id: customerDoc.id,
        ...customerDoc.data(),
        bodyMetrics,
        orders,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update customer
export const updateCustomer = createAsyncThunk(
  'customer/updateCustomer',
  async ({ customerId, updates }, { rejectWithValue }) => {
    try {
      const customerRef = doc(db, 'users', customerId);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(customerRef, updateData);

      return { id: customerId, ...updateData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to add body metrics entry
export const addBodyMetrics = createAsyncThunk(
  'customer/addBodyMetrics',
  async ({ customerId, metricsData }, { rejectWithValue }) => {
    try {
      const metricsRef = collection(db, 'users', customerId, 'bodyMetrics');
      const docRef = await addDoc(metricsRef, {
        ...metricsData,
        createdAt: new Date().toISOString(),
      });

      return { id: docRef.id, ...metricsData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch associates for dropdown
export const fetchAssociates = createAsyncThunk(
  'customer/fetchAssociates',
  async (_, { rejectWithValue }) => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'associate'));
      const querySnapshot = await getDocs(q);

      const associates = [];
      querySnapshot.forEach((doc) => {
        associates.push({
          id: doc.id,
          name: doc.data().name,
          mobile: doc.data().mobile,
          associateId: doc.data().associateId || doc.id,
        });
      });

      return associates;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update subscription
export const updateSubscription = createAsyncThunk(
  'customer/updateSubscription',
  async ({ customerId, subscriptionData }, { rejectWithValue }) => {
    try {
      const customerRef = doc(db, 'users', customerId);
      await updateDoc(customerRef, {
        subscription: subscriptionData,
        updatedAt: new Date().toISOString(),
      });

      return subscriptionData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update shake quantity
export const updateShakeQuantity = createAsyncThunk(
  'customer/updateShakeQuantity',
  async ({ customerId, shakeData }, { rejectWithValue }) => {
    try {
      const customerRef = doc(db, 'users', customerId);
      await updateDoc(customerRef, {
        shakeQuantity: shakeData,
        updatedAt: new Date().toISOString(),
      });

      return shakeData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Customer form data
  formData: {
    // Personal Information
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    role: 'customer',

    // Associate Information
    underAssociate: '',
    associateId: '',
    preferredCustomerId: '',

    // Marital & Family
    maritalStatus: 'single',
    numberOfChildren: 0,

    // Female specific
    pregnancyStatus: 'not_pregnant',
    pregnancyDetails: '',

    // Health Information
    height: '',
    bloodGroup: '',
    medicalRecords: [],
    medicineIntake: [],
    healthRecords: '',
    injuryRecords: '',

    // Reference
    referredBy: '',
    referenceName: '',
    referenceMobile: '',
    referenceLocation: '',

    // Verification
    isVerified: false,
  },

  // Current customer details
  currentCustomer: null,

  // Associates list
  associates: [],

  // Body metrics history
  bodyMetricsHistory: [],

  // Orders history
  ordersHistory: [],

  // Loading states
  isLoading: false,
  isSaving: false,
  error: null,
  success: false,
};

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    // Update form field
    updateFormField: (state, action) => {
      const { field, value } = action.payload;
      state.formData[field] = value;

      // Auto-calculate age from date of birth
      if (field === 'dateOfBirth' && value) {
        const today = new Date();
        const birthDate = new Date(value);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        state.formData.age = age;
      }
    },

    // Add medical record
    addMedicalRecord: (state, action) => {
      state.formData.medicalRecords.push(action.payload);
    },

    // Remove medical record
    removeMedicalRecord: (state, action) => {
      state.formData.medicalRecords.splice(action.payload, 1);
    },

    // Add medicine intake
    addMedicineIntake: (state, action) => {
      state.formData.medicineIntake.push(action.payload);
    },

    // Remove medicine intake
    removeMedicineIntake: (state, action) => {
      state.formData.medicineIntake.splice(action.payload, 1);
    },

    // Reset form
    resetForm: (state) => {
      state.formData = initialState.formData;
      state.error = null;
      state.success = false;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear success
    clearSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Create Customer
    builder
      .addCase(createCustomer.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = true;
        state.currentCustomer = action.payload;
        state.error = null;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
        state.success = false;
      });

    // Fetch Customer Details
    builder
      .addCase(fetchCustomerDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCustomer = action.payload;
        state.bodyMetricsHistory = action.payload.bodyMetrics || [];
        state.ordersHistory = action.payload.orders || [];
        state.error = null;
      })
      .addCase(fetchCustomerDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update Customer
    builder
      .addCase(updateCustomer.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = true;
        if (state.currentCustomer) {
          state.currentCustomer = { ...state.currentCustomer, ...action.payload };
        }
        state.error = null;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });

    // Add Body Metrics
    builder
      .addCase(addBodyMetrics.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(addBodyMetrics.fulfilled, (state, action) => {
        state.isSaving = false;
        state.bodyMetricsHistory.unshift(action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(addBodyMetrics.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });

    // Fetch Associates
    builder
      .addCase(fetchAssociates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssociates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.associates = action.payload;
        state.error = null;
      })
      .addCase(fetchAssociates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update Subscription
    builder
      .addCase(updateSubscription.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.isSaving = false;
        if (state.currentCustomer) {
          state.currentCustomer.subscription = action.payload;
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });

    // Update Shake Quantity
    builder
      .addCase(updateShakeQuantity.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateShakeQuantity.fulfilled, (state, action) => {
        state.isSaving = false;
        if (state.currentCustomer) {
          state.currentCustomer.shakeQuantity = action.payload;
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateShakeQuantity.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateFormField,
  addMedicalRecord,
  removeMedicalRecord,
  addMedicineIntake,
  removeMedicineIntake,
  resetForm,
  clearError,
  clearSuccess,
} = customerSlice.actions;

export default customerSlice.reducer;
